import React, { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

// types
import { FutureReservationsResponse } from '../../../../types/types'

// utils
import { PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'
import { FUTURE_RESERVATIONS_LIMIT_MY_RESERVATIONS_PAGE } from '../../../../utils/enums'
import { MyLocationContext } from '../../../../utils/myLocationProvider'

// components
import ErrorScreen from '../../../../atoms/ErrorScreen/ErrorScreen'
import Empty from '../../../../atoms/Empty/Empty'
import ReservationsSlider from '../../../../components/ReservationsSlider/ReservationsSlider'
import ReservationCard, { ReservationCardSkeleton } from '../../../../components/ReservationCard/ReservationCard'

// assets
import EmptyCalendarIcon from '../../../../assets/icons/EmptyCalendarIcon'

// styles
import * as SC from './FutureReservationsStyles'

// hooks
import useMessages from '../../../../hooks/useMessages'

const FUTURE_RESERVATIONS_SLIDER_BREAKPOINT = 'sm'

const FutureReservations = () => {
	const { locale } = useIntl()
	const { messages } = useMessages()
	const { apiBrowser } = useContext(AppContext)
	const { myLocation } = useContext(MyLocationContext)

	const [futureReservations, setFutureReservations] = useState<FutureReservationsResponse | undefined>()
	const [isLoading, setIsLoading] = useState(true)
	const [refetchFutureReservations, setRefetchFutureReservations] = useState(false)

	useEffect(() => {
		const fetchFutureReservationsData = async () => {
			try {
				setIsLoading(true)
				const futureReservationsResponse = await apiBrowser.b2c.getFutureReservations({
					page: 1,
					limit: FUTURE_RESERVATIONS_LIMIT_MY_RESERVATIONS_PAGE
				})
				setFutureReservations(futureReservationsResponse)
			} catch {
				setFutureReservations(undefined)
			} finally {
				setIsLoading(false)
			}
		}

		fetchFutureReservationsData()
	}, [apiBrowser, refetchFutureReservations])

	const handleRetryFetchingFutureReservations = () => {
		setRefetchFutureReservations(true)
	}

	const onExploreSalonsClick = () => {
		window.location.href = PAGE_LINKS['/salons/all'](locale, { ...myLocation })
	}

	if (isLoading) {
		const skeletonItems = Array.from({ length: 4 }, (_, index) => <ReservationCardSkeleton key={index} cardStyle='future' />)

		return (
			<>
				<SC.MobileSlider $breakpoint={FUTURE_RESERVATIONS_SLIDER_BREAKPOINT}>
					<ReservationsSlider slides={skeletonItems} mobileBreakpoint={FUTURE_RESERVATIONS_SLIDER_BREAKPOINT} />
				</SC.MobileSlider>

				<SC.FutureReservationsGrid $breakpoint={FUTURE_RESERVATIONS_SLIDER_BREAKPOINT}>{skeletonItems}</SC.FutureReservationsGrid>
			</>
		)
	}

	if (typeof futureReservations === 'undefined') {
		return <ErrorScreen button={{ onClick: handleRetryFetchingFutureReservations }} />
	}

	if (!futureReservations.reservations.length) {
		return (
			<Empty
				title={messages['You don’t have any planned reservations']}
				icon={<EmptyCalendarIcon />}
				button={{ label: messages['Explore salons'], onClick: onExploreSalonsClick }}
			/>
		)
	}

	const reservationItems = futureReservations.reservations.map((reservationData) => (
		<ReservationCard
			key={reservationData.id}
			reservationData={reservationData}
			cardStyle={'future'}
			href={PAGE_LINKS['/salons/my-reservations/:reservationID'](locale, reservationData.id)}
		/>
	))

	return (
		<>
			<SC.MobileSlider $breakpoint={FUTURE_RESERVATIONS_SLIDER_BREAKPOINT}>
				<ReservationsSlider slides={reservationItems} mobileBreakpoint={FUTURE_RESERVATIONS_SLIDER_BREAKPOINT} />
			</SC.MobileSlider>
			<SC.FutureReservationsGrid $breakpoint={FUTURE_RESERVATIONS_SLIDER_BREAKPOINT}>{reservationItems}</SC.FutureReservationsGrid>
		</>
	)
}

export default FutureReservations
